/**
 * Consent + suppression — record only, WP-W11. `web/shapes/outreach.md` §5.
 *
 * Task 18's API has no `GET` for either — only `POST /outreach/consent` and
 * `POST /outreach/suppress` — so this panel offers exactly what the API offers: two
 * small forms, each candidate targeted BY ID (the same picker-or-id-input pattern
 * every other create form on this screen uses), and a confirmation on success. It
 * does not claim to show "current consents" or "current suppressions" — that would
 * be a read this API does not have.
 *
 * Suppression is idempotent: adding the same candidate+channel twice returns the
 * existing row rather than an error, so a repeat is still shown as the success case.
 */

import { useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import type {
  OutreachChannel,
  OutreachConsent,
  OutreachConsentBasis,
  OutreachConsentCreateRequest,
  OutreachSuppression,
  OutreachSuppressionCreateRequest,
} from '../../api/outreach';
import { OUTREACH_CHANNELS, OUTREACH_CONSENT_BASES } from '../../api/outreach';
import { OUTREACH_CHANNEL_LABEL, OUTREACH_CONSENT_BASIS_LABEL } from '../../recruiting/outreach';
import type { WorkforceApiError } from '../../api/errors';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { StatusNote } from '../../states/StatusNote';
import type { PickerOption } from './CreateApplicationForm';

export interface OutreachCompliancePanelProps {
  readonly candidateOptions: readonly PickerOption[] | null;

  readonly consentPending: boolean;
  readonly consentError: WorkforceApiError | null;
  readonly consentRecorded: OutreachConsent | null;
  readonly onRecordConsent: (body: OutreachConsentCreateRequest) => void;

  readonly suppressPending: boolean;
  readonly suppressError: WorkforceApiError | null;
  readonly suppressed: OutreachSuppression | null;
  readonly onSuppress: (body: OutreachSuppressionCreateRequest) => void;
}

function CandidateField({
  id,
  candidateOptions,
  value,
  onChange,
}: {
  readonly id: string;
  readonly candidateOptions: readonly PickerOption[] | null;
  readonly value: string;
  readonly onChange: (value: string) => void;
}): ReactNode {
  const { t } = useI18n();
  if (candidateOptions === null) {
    return (
      <>
        <input
          className="wp-field__input"
          id={id}
          data-testid={id}
          type="text"
          value={value}
          autoComplete="off"
          onChange={(event) => {
            onChange(event.target.value);
          }}
        />
        <p className="wp-small wp-muted">{t('outreach.messages.create.candidateIdHint')}</p>
      </>
    );
  }
  return (
    <select
      className="wp-field__input"
      id={id}
      data-testid={id}
      value={value}
      onChange={(event) => {
        onChange(event.target.value);
      }}
    >
      <option value="">{t('outreach.messages.create.candidatePlaceholder')}</option>
      {candidateOptions.map((option) => (
        <option key={option.id} value={option.id}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

export function OutreachCompliancePanel(props: OutreachCompliancePanelProps): ReactNode {
  const { t } = useI18n();

  const [consentCandidateId, setConsentCandidateId] = useState('');
  const [consentChannel, setConsentChannel] = useState<OutreachChannel>('email');
  const [consentBasis, setConsentBasis] = useState<OutreachConsentBasis>('explicit_optin');
  const [consentNote, setConsentNote] = useState('');
  const consentReady = consentCandidateId.trim() !== '';

  const [suppressCandidateId, setSuppressCandidateId] = useState('');
  const [suppressChannel, setSuppressChannel] = useState<OutreachChannel>('email');
  const [suppressReason, setSuppressReason] = useState('');
  const suppressReady = suppressCandidateId.trim() !== '';

  return (
    <section
      className="wp-section"
      aria-labelledby="w11-compliance-heading"
      data-testid="w11-compliance"
    >
      <h2 className="wp-section__title" id="w11-compliance-heading">
        {t('outreach.compliance.title')}
      </h2>

      <div className="wp-section" data-testid="w11-consent">
        <h3 className="wp-section__title">{t('outreach.consent.title')}</h3>
        <p className="wp-small wp-muted">{t('outreach.consent.intro')}</p>
        <form
          className="wp-filters"
          onSubmit={(event) => {
            event.preventDefault();
            if (!consentReady || props.consentPending) return;
            const note = consentNote.trim();
            props.onRecordConsent({
              candidate_id: consentCandidateId.trim(),
              channel: consentChannel,
              basis: consentBasis,
              ...(note === '' ? {} : { note }),
            });
          }}
        >
          <div className="wp-filters__group">
            <label className="wp-field__label" htmlFor="w11-consent-candidate">
              {t('outreach.messages.create.candidate')}
            </label>
            <CandidateField
              id="w11-consent-candidate"
              candidateOptions={props.candidateOptions}
              value={consentCandidateId}
              onChange={setConsentCandidateId}
            />
          </div>
          <div className="wp-filters__group">
            <label className="wp-field__label" htmlFor="w11-consent-channel">
              {t('outreach.messages.create.channel')}
            </label>
            <select
              className="wp-field__input"
              id="w11-consent-channel"
              data-testid="w11-consent-channel"
              value={consentChannel}
              onChange={(event) => {
                setConsentChannel(event.target.value as OutreachChannel);
              }}
            >
              {OUTREACH_CHANNELS.map((value) => (
                <option key={value} value={value}>
                  {t(OUTREACH_CHANNEL_LABEL[value])}
                </option>
              ))}
            </select>
          </div>
          <div className="wp-filters__group">
            <label className="wp-field__label" htmlFor="w11-consent-basis">
              {t('outreach.consent.basis.label')}
            </label>
            <select
              className="wp-field__input"
              id="w11-consent-basis"
              data-testid="w11-consent-basis"
              value={consentBasis}
              onChange={(event) => {
                setConsentBasis(event.target.value as OutreachConsentBasis);
              }}
            >
              {OUTREACH_CONSENT_BASES.map((value) => (
                <option key={value} value={value}>
                  {t(OUTREACH_CONSENT_BASIS_LABEL[value])}
                </option>
              ))}
            </select>
          </div>
          <div className="wp-filters__group">
            <label className="wp-field__label" htmlFor="w11-consent-note">
              {t('outreach.consent.note')}
            </label>
            <input
              className="wp-field__input"
              id="w11-consent-note"
              data-testid="w11-consent-note"
              type="text"
              value={consentNote}
              maxLength={2000}
              autoComplete="off"
              onChange={(event) => {
                setConsentNote(event.target.value);
              }}
            />
          </div>
          <div className="wp-filters__actions">
            <button
              className="wp-button wp-button--primary"
              type="submit"
              data-testid="w11-consent-submit"
              disabled={!consentReady || props.consentPending}
            >
              {props.consentPending
                ? t('outreach.consent.submitting')
                : t('outreach.consent.submit')}
            </button>
          </div>
        </form>
        {props.consentError === null ? null : (
          <ApiErrorNote
            error={props.consentError}
            subject={t('outreach.consent.subjectWord')}
            testId="w11-consent-error"
          />
        )}
        {props.consentRecorded === null ? null : (
          <StatusNote severity="low" title={t('outreach.consent.ok')} testId="w11-consent-ok" />
        )}
      </div>

      <div className="wp-section" data-testid="w11-suppress">
        <h3 className="wp-section__title">{t('outreach.suppress.title')}</h3>
        <p className="wp-small wp-muted">{t('outreach.suppress.intro')}</p>
        <form
          className="wp-filters"
          onSubmit={(event) => {
            event.preventDefault();
            if (!suppressReady || props.suppressPending) return;
            const reason = suppressReason.trim();
            props.onSuppress({
              candidate_id: suppressCandidateId.trim(),
              channel: suppressChannel,
              ...(reason === '' ? {} : { reason }),
            });
          }}
        >
          <div className="wp-filters__group">
            <label className="wp-field__label" htmlFor="w11-suppress-candidate">
              {t('outreach.messages.create.candidate')}
            </label>
            <CandidateField
              id="w11-suppress-candidate"
              candidateOptions={props.candidateOptions}
              value={suppressCandidateId}
              onChange={setSuppressCandidateId}
            />
          </div>
          <div className="wp-filters__group">
            <label className="wp-field__label" htmlFor="w11-suppress-channel">
              {t('outreach.messages.create.channel')}
            </label>
            <select
              className="wp-field__input"
              id="w11-suppress-channel"
              data-testid="w11-suppress-channel"
              value={suppressChannel}
              onChange={(event) => {
                setSuppressChannel(event.target.value as OutreachChannel);
              }}
            >
              {OUTREACH_CHANNELS.map((value) => (
                <option key={value} value={value}>
                  {t(OUTREACH_CHANNEL_LABEL[value])}
                </option>
              ))}
            </select>
          </div>
          <div className="wp-filters__group">
            <label className="wp-field__label" htmlFor="w11-suppress-reason">
              {t('outreach.suppress.reason')}
            </label>
            <input
              className="wp-field__input"
              id="w11-suppress-reason"
              data-testid="w11-suppress-reason"
              type="text"
              value={suppressReason}
              maxLength={2000}
              autoComplete="off"
              onChange={(event) => {
                setSuppressReason(event.target.value);
              }}
            />
          </div>
          <div className="wp-filters__actions">
            <button
              className="wp-button wp-button--primary"
              type="submit"
              data-testid="w11-suppress-submit"
              disabled={!suppressReady || props.suppressPending}
            >
              {props.suppressPending
                ? t('outreach.suppress.submitting')
                : t('outreach.suppress.submit')}
            </button>
          </div>
        </form>
        {props.suppressError === null ? null : (
          <ApiErrorNote
            error={props.suppressError}
            subject={t('outreach.suppress.subjectWord')}
            testId="w11-suppress-error"
          />
        )}
        {props.suppressed === null ? null : (
          <StatusNote severity="low" title={t('outreach.suppress.ok')} testId="w11-suppress-ok" />
        )}
      </div>
    </section>
  );
}
